<script setup>
// Extrato detalhado do consumo (em R$): cada busca, enriquecimento, envio e
// tokens — débitos da carteira pré-paga. Lê credits/transactions com paginação
// (10 por página). Valores com até 4 casas (consumo pode ser < 1 centavo).
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import creditsAPI from 'dashboard/api/credits';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();

const PAGE_SIZE = 10;

const items = ref([]);
const total = ref(0);
const page = ref(0); // 0-based
const isLoading = ref(true);

const KIND_LABEL = {
  enrich_company: 'Consulta empresa',
  enrich_socio: 'Consulta sócio (decisor)',
  prospect_send: 'Envio de prospecção',
  message_send: 'Envio de mensagem',
  llm_tokens: 'Tokens de IA',
  search_lead: 'Busca de leads',
  voice_ai: 'Chamada de voz (IA)',
  ai_chat: 'Chat com IA',
  ai_image: 'Imagem gerada por IA',
  sms: 'SMS',
  rcs: 'RCS',
  social_x: 'X (Twitter)',
  recharge: 'Recarga',
  adjust: 'Ajuste',
};

// Sempre 4 casas no extrato: consumo pode ser < 1 centavo (ex.: R$ 0,0030).
const brl = v =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(v || 0);

const fmtDate = iso => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const label = kind => KIND_LABEL[kind] || kind;

const totalPages = computed(() =>
  Math.max(1, Math.ceil(total.value / PAGE_SIZE))
);
const rangeStart = computed(() =>
  total.value ? page.value * PAGE_SIZE + 1 : 0
);
const rangeEnd = computed(() =>
  Math.min(total.value, (page.value + 1) * PAGE_SIZE)
);
const canPrev = computed(() => page.value > 0);
const canNext = computed(() => page.value + 1 < totalPages.value);

const fetchPage = async () => {
  isLoading.value = true;
  try {
    const { data } = await creditsAPI.transactions({
      limit: PAGE_SIZE,
      offset: page.value * PAGE_SIZE,
    });
    items.value = data.transactions || [];
    total.value = data.total ?? items.value.length;
  } catch {
    items.value = [];
    total.value = 0;
  } finally {
    isLoading.value = false;
  }
};

const prev = () => {
  if (!canPrev.value) return;
  page.value -= 1;
  fetchPage();
};

const next = () => {
  if (!canNext.value) return;
  page.value += 1;
  fetchPage();
};

onMounted(fetchPage);
</script>

<template>
  <div class="bg-n-solid-2 rounded-2xl border border-n-weak overflow-hidden">
    <div
      class="px-6 py-4 border-b border-n-weak bg-n-alpha-1 flex items-center justify-between"
    >
      <h2 class="text-lg font-semibold text-n-slate-12">
        {{ t('BILLING.CONSUMPTION.STATEMENT_TITLE') }}
      </h2>
      <span v-if="total" class="text-xs text-n-slate-10">
        {{
          t('BILLING.CONSUMPTION.STATEMENT_RANGE', {
            start: rangeStart,
            end: rangeEnd,
            total,
          })
        }}
      </span>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-10">
      <Spinner :size="24" />
    </div>

    <p
      v-else-if="!items.length"
      class="px-6 py-8 text-center text-sm text-n-slate-10"
    >
      {{ t('BILLING.CONSUMPTION.STATEMENT_EMPTY') }}
    </p>

    <template v-else>
      <div class="divide-y divide-n-weak">
        <div
          v-for="tx in items"
          :key="tx.id"
          class="flex items-center justify-between px-6 py-3"
        >
          <div class="min-w-0">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ label(tx.kind) }}
            </p>
            <p class="text-xs text-n-slate-10 truncate">
              {{ tx.description || '—' }} · {{ fmtDate(tx.created_at) }}
            </p>
          </div>
          <span
            class="text-sm font-semibold shrink-0 ml-3"
            :class="tx.brl < 0 ? 'text-n-ruby-11' : 'text-n-teal-11'"
          >
            {{ brl(tx.brl) }}
          </span>
        </div>
      </div>

      <div
        v-if="totalPages > 1"
        class="flex items-center justify-between px-6 py-3 border-t border-n-weak"
      >
        <button
          class="text-sm font-medium text-n-slate-11 disabled:opacity-40 disabled:cursor-not-allowed hover:text-n-slate-12"
          :disabled="!canPrev"
          @click="prev"
        >
          {{ t('BILLING.CONSUMPTION.STATEMENT_PREV') }}
        </button>
        <span class="text-xs text-n-slate-10">
          {{
            t('BILLING.CONSUMPTION.STATEMENT_PAGE', {
              current: page + 1,
              total: totalPages,
            })
          }}
        </span>
        <button
          class="text-sm font-medium text-n-slate-11 disabled:opacity-40 disabled:cursor-not-allowed hover:text-n-slate-12"
          :disabled="!canNext"
          @click="next"
        >
          {{ t('BILLING.CONSUMPTION.STATEMENT_NEXT') }}
        </button>
      </div>
    </template>
  </div>
</template>
